import React from 'react';
import { useForm } from 'react-hook-form';
import { useAuth } from '../../../hooks/useAuth';
import { updateUserPreferences } from '../../../utils/userService';
import { toast } from 'react-hot-toast';
import { Globe, Moon, Sun, Bell } from 'lucide-react';
import { CURRENCIES } from '../../../utils/currencyUtils';

interface PreferencesFormData {
  currency: string;
  theme: 'light' | 'dark';
  notifications: boolean;
}

export function PreferencesTab() {
  const { user } = useAuth();
  const { register, handleSubmit } = useForm<PreferencesFormData>({
    defaultValues: {
      currency: user?.preferences?.currency || 'USD',
      theme: user?.preferences?.theme || 'light',
      notifications: user?.preferences?.notifications ?? true
    }
  });

  const onSubmit = async (data: PreferencesFormData) => {
    if (!user) return;
    try {
      await updateUserPreferences(user.uid, data);
      toast.success('Preferences updated successfully');
    } catch (error) {
      toast.error('Failed to update preferences');
      console.error('Error updating preferences:', error);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div className="grid grid-cols-1 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 flex items-center gap-2">
            <Globe className="w-4 h-4 text-gray-400" />
            Default Currency
          </label>
          <select
            {...register('currency')}
            className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
          >
            {CURRENCIES.map(currency => (
              <option key={currency.code} value={currency.code}>
                {currency.symbol} {currency.name} ({currency.code})
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 flex items-center gap-2">
            {user?.preferences?.theme === 'dark' ? (
              <Moon className="w-4 h-4 text-gray-400" />
            ) : (
              <Sun className="w-4 h-4 text-gray-400" />
            )}
            Theme
          </label>
          <select
            {...register('theme')}
            className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="light">Light</option>
            <option value="dark">Dark</option>
          </select>
        </div>

        <div>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              {...register('notifications')}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <span className="text-sm font-medium text-gray-700 flex items-center gap-2">
              <Bell className="w-4 h-4 text-gray-400" />
              Receive notifications and updates
            </span>
          </label>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          type="submit"
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Save Preferences
        </button>
      </div>
    </form>
  );
}